export type WordleStrings = {
  navbar: {
    prevDayLabel: string;
    nextDayLabel: string;
    howToPlayLabel: string;
    datePickerLabel: string;
    settingsLabel: string;
    statsLabel: string;
    autoLabel: string;
    hardcoreLabel: string;
    hardcoreDisabledHint: string;
    playedInHardcoreLabel: string;
  };
  hardcore: {
    timerLabel: string;
    failTitle: string;
    failSubline: string;
    alreadyPlayedInHardcore: string;
    alreadyPlayedInNormal: string;
  };
  datePicker: {
    title: string;
    prevMonthLabel: string;
    nextMonthLabel: string;
    closeLabel: string;
    months: string[];
    days: string[];
    hardcoreLabel: string;
  };
  stats: {
    title: string;
    closeLabel: string;
    gamesLabelPlural: string;
    gamesLabelSingular: string;
    winRateLabel: string;
    currentStreakLabel: string;
    bestStreakLabel: string;
    performanceTitle: string;
    lossLabel: string;
    timeTitle: string;
    bestTimeLabel: string;
    avgTimeLabel: string;
    worstTimeLabel: string;
  };
  settings: {
    title: string;
    supportTitle: string;
    supportBody: string;
    supportCta: string;
    creditsTitle: string;
    creditsGameBy: string;
    creditsDesignInspiredBy: string;
    closeLabel: string;
  };
  gameOver: {
    title: string;
    wordWas: string;
    subline: string;
    closeLabel: string;
  };
  win: {
    perfectBadge: string;
    perfectTitle: string;
    winTitle: string;
    nextWordInLabel: string;
    closeLabel: string;
  };
  howToPlay: {
    title: string;
    closeLabel: string;
    introLine1: string;
    introLine2: string;
    colorHint: string;
    examples: Array<{
      word: string[];
      highlightIndex: number;
      color: 'correct' | 'present' | 'absent';
      label: string;
    }>;
  };
  feedbacks: {
    perfect: string[];
    veryHot: string[];
    hot: string[];
    warm: string[];
    cold: string[];
  };
};

export const FR_WORDLE_STRINGS: WordleStrings = {
  navbar: {
    prevDayLabel: 'Jour précédent',
    nextDayLabel: 'Jour suivant',
    howToPlayLabel: 'Comment jouer',
    datePickerLabel: 'Sélectionner une date',
    settingsLabel: 'Paramètres',
    statsLabel: 'Stats',
    autoLabel: 'Auto',
    hardcoreLabel: 'Hardcore',
    hardcoreDisabledHint: 'Partie du jour terminée — mode hardcore indisponible',
    playedInHardcoreLabel: 'Le mot a été joué en mode hardcore, c’était',
  },
  hardcore: {
    timerLabel: 'Temps restant',
    failTitle: 'Raté',
    failSubline: 'Le mode hardcore ne pardonne pas.',
    alreadyPlayedInHardcore: 'Déjà joué en mode Hardcore aujourd’hui.',
    alreadyPlayedInNormal: 'Déjà joué en mode Normal aujourd’hui.',
  },
  datePicker: {
    title: 'Sélectionner une date',
    prevMonthLabel: 'Mois précédent',
    nextMonthLabel: 'Mois suivant',
    closeLabel: 'Fermer',
    months: [
      'Janvier',
      'Février',
      'Mars',
      'Avril',
      'Mai',
      'Juin',
      'Juillet',
      'Août',
      'Septembre',
      'Octobre',
      'Novembre',
      'Décembre',
    ],
    days: ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'],
    hardcoreLabel: 'Joué en mode hardcore',
  },
  stats: {
    title: 'Statistiques',
    closeLabel: 'Fermer',
    gamesLabelPlural: 'parties',
    gamesLabelSingular: 'partie',
    winRateLabel: 'victoires (%)',
    currentStreakLabel: 'série actuelle',
    bestStreakLabel: 'meilleure série',
    performanceTitle: 'Performances',
    lossLabel: '💀',
    timeTitle: 'Chrono hardcore (temps restant)',
    bestTimeLabel: 'meilleur',
    avgTimeLabel: 'moyen',
    worstTimeLabel: 'pire',
  },
  settings: {
    title: 'Paramètres',
    supportTitle: 'Soutenir le projet',
    supportBody:
      'Si tu apprécies le jeu et que tu veux soutenir son développement, tu peux m’offrir un café ☕',
    supportCta: 'Me payer un café',
    creditsTitle: 'Crédits',
    creditsGameBy: 'Jeu développé par',
    creditsDesignInspiredBy: 'Design inspiré de',
    closeLabel: 'Fermer',
  },
  gameOver: {
    title: 'Dommage !',
    wordWas: 'Le mot était',
    subline: 'Il était pas si compliqué pourtant…',
    closeLabel: 'Fermer',
  },
  win: {
    perfectBadge: '🏆 PERFECT !',
    perfectTitle: 'Coup parfait !',
    winTitle: '🎉 Bravo !',
    nextWordInLabel: 'Prochain mot dans',
    closeLabel: 'Fermer',
  },
  howToPlay: {
    title: 'Comment jouer ?',
    closeLabel: 'Fermer',
    introLine1: 'Devine le mot mystère en 6 essais.',
    introLine2: 'Le mot est composé de 5 lettres.',
    colorHint:
      'À chaque tentative, la couleur des lettres change pour t’indiquer si tu es proche de la solution.',
    examples: [
      {
        word: ['F', 'R', 'U', 'I', 'T'],
        highlightIndex: 0,
        color: 'correct',
        label: 'La lettre F est dans le mot et à la bonne place.',
      },
      {
        word: ['P', 'O', 'C', 'H', 'E'],
        highlightIndex: 2,
        color: 'present',
        label: 'La lettre C est dans le mot mais mal placée.',
      },
      {
        word: ['S', 'O', 'E', 'U', 'R'],
        highlightIndex: 4,
        color: 'absent',
        label: 'La lettre R n’est pas dans le mot.',
      },
    ],
  },
  feedbacks: {
    perfect: [
      '🏆 PERFECT !',
      '🔥 Incroyable',
      '🚀 Magistral',
      '💎 Sans faute',
      '⚡ Génie pur',
      '🎯 Dans le mille',
    ],
    veryHot: [
      '🔥 Très chaud',
      '💥 Tu brûles',
      '🌋 Éruption imminente',
      '😮‍💨 À un cheveu',
      '👁️‍🗨️ Tu vois clair',
      '🚨 Danger proche',
    ],
    hot: [
      '🌡️ Pas loin',
      '👀 Bien vu',
      '🧠 Bonne intuition',
      '🎯 Tu te rapproches',
      '📍 Dans la zone',
      '😏 Presque ça',
    ],
    warm: [
      '🙂 Ça avance',
      '🟡 Correct',
      '🧩 Pas mal',
      '🤔 Intéressant…',
      '🔍 Continue comme ça',
      '📈 En progression',
    ],
    cold: [
      '❄️ Glacial',
      '🥶 Loin…',
      '🌨️ Brrr…',
      '🧊 Très froid',
      '😬 À côté de la plaque',
      '🫥 Aucun signal',
      '😏 Tu fais exprès ?',
      '🫣 Oups…',
    ],
  },
};

export const EN_WORDLE_STRINGS: WordleStrings = {
  navbar: {
    prevDayLabel: 'Previous day',
    nextDayLabel: 'Next day',
    howToPlayLabel: 'How to play',
    datePickerLabel: 'Select a date',
    settingsLabel: 'Settings',
    statsLabel: 'Stats',
    autoLabel: 'Auto',
    hardcoreLabel: 'Hardcore',
    hardcoreDisabledHint: "Today's game is over — hardcore mode unavailable",
    playedInHardcoreLabel: 'This word was played in hardcore mode, it was',
  },
  hardcore: {
    timerLabel: 'Time left',
    failTitle: 'Failed',
    failSubline: 'No mercy in hardcore mode.',
    alreadyPlayedInHardcore: 'Already played in Hardcore mode today.',
    alreadyPlayedInNormal: 'Already played in Normal mode today.',
  },
  datePicker: {
    title: 'Select a date',
    prevMonthLabel: 'Previous month',
    nextMonthLabel: 'Next month',
    closeLabel: 'Close',
    months: [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ],
    days: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    hardcoreLabel: 'Played in hardcore mode',
  },
  stats: {
    title: 'Statistics',
    closeLabel: 'Close',
    gamesLabelPlural: 'games',
    gamesLabelSingular: 'game',
    winRateLabel: 'win rate (%)',
    currentStreakLabel: 'current streak',
    bestStreakLabel: 'best streak',
    performanceTitle: 'Performance',
    lossLabel: '💀',
    timeTitle: 'Hardcore timer (time left)',
    bestTimeLabel: 'best',
    avgTimeLabel: 'avg',
    worstTimeLabel: 'worst',
  },
  settings: {
    title: 'Settings',
    supportTitle: 'Support the project',
    supportBody:
      'If you enjoy the game and want to support its development, you can buy me a coffee ☕',
    supportCta: 'Buy me a coffee',
    creditsTitle: 'Credits',
    creditsGameBy: 'Game developed by',
    creditsDesignInspiredBy: 'Design inspired by',
    closeLabel: 'Close',
  },
  gameOver: {
    title: 'Too bad!',
    wordWas: 'The word was',
    subline: 'It wasn’t that hard though…',
    closeLabel: 'Close',
  },
  win: {
    perfectBadge: '🏆 PERFECT!',
    perfectTitle: 'Perfect game!',
    winTitle: '🎉 Well done!',
    nextWordInLabel: 'Next word in',
    closeLabel: 'Close',
  },
  howToPlay: {
    title: 'How to play?',
    closeLabel: 'Close',
    introLine1: 'Guess the mystery word in 6 tries.',
    introLine2: 'The word is 5 letters long.',
    colorHint:
      'Each guess changes the letter colors to show how close you are to the solution.',
    examples: [
      {
        word: ['S', 'H', 'A', 'R', 'E'],
        highlightIndex: 0,
        color: 'correct',
        label: 'The letter S is in the word and in the correct spot.',
      },
      {
        word: ['C', 'R', 'A', 'N', 'E'],
        highlightIndex: 2,
        color: 'present',
        label: 'The letter A is in the word but in the wrong spot.',
      },
      {
        word: ['M', 'O', 'U', 'S', 'E'],
        highlightIndex: 4,
        color: 'absent',
        label: 'The letter E is not in the word.',
      },
    ],
  },
  feedbacks: {
    perfect: [
      '🏆 PERFECT!',
      '🔥 Incredible',
      '🚀 Brilliant',
      '💎 Flawless',
      '⚡ Genius',
      '🎯 Bullseye',
    ],
    veryHot: [
      '🔥 Very hot',
      '💥 You are burning',
      '🌋 About to erupt',
      '😮‍💨 So close',
      '👁️‍🗨️ Clear sight',
      '🚨 Danger nearby',
    ],
    hot: [
      '🌡️ Not far',
      '👀 Nice one',
      '🧠 Good intuition',
      '🎯 Getting closer',
      '📍 In the zone',
      '😏 Almost there',
    ],
    warm: [
      '🙂 Progress',
      '🟡 Decent',
      '🧩 Not bad',
      '🤔 Interesting…',
      '🔍 Keep going',
      '📈 Trending up',
    ],
    cold: [
      '❄️ Freezing',
      '🥶 Far away…',
      '🌨️ Brrr…',
      '🧊 Very cold',
      '😬 Off the mark',
      '🫥 No signal',
      '😏 On purpose?',
      '🫣 Oops…',
    ],
  },
};
